import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, IconButton, TextField } from '@material-ui/core';
import { Modal, ModalBody, ModalHeader, Footer } from './styles';
import Botao from '../Botoes/Botao';
import InputNumero from '../InputNumero';
import useStore from '../../services/hooks/useStore';
import NewSelect from '../Select/newSelect';
import CloseIcon from '@material-ui/icons/Close';
import Formatter from '../../services/Formatter';
import { apiV2 } from '../../services/utils';
import X9Logger from '../../services/X9Logger';

export type NfeAvulsaItem = {
  codigoProduto: string;
  descricaoProduto: string;
  unidade: string;
  quantidade: number;
  valorUnitario: number;
  valorTotal: number;
  codigoNcm: string;
  cfop: string;
  ibscbsCst: string;
  ibscbsClasse: string;
  icmsCst: string;
  icmsPercentual: number;
  ipiCst: string;
  ipiPercentual: number;
  cofinsCst: string;
  cofinsPercentual: number;
  pisCst: string;
  pisPercentual: number;
};

type Props = {
  open: boolean;
  handleClose: () => void;
  onSave: (item: NfeAvulsaItem) => void;
  item?: NfeAvulsaItem | null;
};

const defaultItem = (): NfeAvulsaItem => ({
  codigoProduto: '',
  descricaoProduto: '',
  unidade: '',
  quantidade: 0,
  valorUnitario: 0,
  valorTotal: 0,
  codigoNcm: '',
  cfop: '',
  ibscbsCst: '',
  ibscbsClasse: '',
  icmsCst: '',
  icmsPercentual: 0,
  ipiCst: '',
  ipiPercentual: 0,
  cofinsCst: '',
  cofinsPercentual: 0,
  pisCst: '',
  pisPercentual: 0,
});

function normalizeItem(raw: NfeAvulsaItem | null | undefined): NfeAvulsaItem {
  const base = defaultItem();
  if (!raw) return base;

  const cstAny = raw.ibscbsCst as unknown;
  const classeAny = raw.ibscbsClasse as unknown;
  const ibscbsCstStr =
    cstAny != null && typeof cstAny === 'object' && 'value' in (cstAny as object)
      ? String((cstAny as SelectRow).value ?? '')
      : String(raw.ibscbsCst ?? '');
  const ibscbsClasseStr =
    classeAny != null && typeof classeAny === 'object' && 'value' in (classeAny as object)
      ? String((classeAny as SelectRow).value ?? '')
      : String(raw.ibscbsClasse ?? '');

  return {
    ...base,
    ...raw,
    ibscbsCst: ibscbsCstStr,
    ibscbsClasse: ibscbsClasseStr,
  };
}

function pickFirstString(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return '';
}

function toSelectRowsFromList(list: unknown, valueKeys: string[], descKeys: string[]): SelectRow[] {
  const raw = (list as { results?: unknown })?.results ?? list;
  const arr = Array.isArray(raw) ? raw : [];
  return arr
    .map((row: any) => {
      const value = pickFirstString(row ?? {}, valueKeys);
      if (!value) return null;
      const desc = pickFirstString(row ?? {}, descKeys);
      return { value, label: desc ? `${value} - ${desc}` : value } as SelectRow;
    })
    .filter(Boolean) as SelectRow[];
}


function prefixoDigitosIbscbsCst(cstValor: string): string {
  const s = String(cstValor ?? '').trim();
  const m = /^(\d+)/.exec(s);
  return m ? m[1] : '';
}

/** Unidades comuns para tag `uCom` da NFe (código comercial). */
const UNIDADE_COMERCIAL_OPTIONS: SelectRow[] = [
  { value: 'UN', label: 'UN — Unidade' },
  { value: 'KG', label: 'KG — Quilograma' },
  { value: 'G', label: 'G — Grama' },
  { value: 'L', label: 'L — Litro' },
  { value: 'M', label: 'M — Metro' },
  { value: 'M2', label: 'M² — Metro quadrado' },
  { value: 'M3', label: 'M³ — Metro cúbico' },
  { value: 'PC', label: 'PC — Peça' },
  { value: 'CX', label: 'CX — Caixa' },
  { value: 'PCT', label: 'PCT — Pacote' },
  { value: 'TON', label: 'TON — Tonelada' },
  { value: 'PAR', label: 'PAR — Par' },
  { value: 'GL', label: 'GL — Galão' },
  { value: 'BD', label: 'BD — Balde' },
  { value: 'SC', label: 'SC — Sachê' },
];

const ModalNFEAvulsaItem: React.FC<Props> = ({ open, handleClose, onSave, item }) => {
  const store = useStore();
  const [form, setForm] = useState<NfeAvulsaItem>(() => normalizeItem(item ?? null));
  const [cfopOptions, setCfopOptions] = useState<SelectRow[]>([]);
  const [ibscbsCstOptions, setIbscbsCstOptions] = useState<SelectRow[]>([]);
  const [ibscbsClasseOptionsAll, setIbscbsClasseOptionsAll] = useState<SelectRow[]>([]);

  useEffect(() => {
    if (!open) return;
    setForm(normalizeItem(item ?? null));
  }, [open, item]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const loadCfop = async () => {
        try {
          const cfopResponse = await apiV2.get('/cfop-nfe/');
          const cfopList = (cfopResponse.data?.results ?? cfopResponse.data ?? []) as any[];
          const cfopRows = (Array.isArray(cfopList) ? cfopList : [])
            .map((cfop: any) => {
              const n = String(cfop?.n_cfop ?? '').trim();
              if (!n) return null;
              const nat = String(cfop?.natureza_operacao ?? '').trim();
              return { label: nat ? `${n} - ${nat}` : n, value: n } as SelectRow;
            })
            .filter(Boolean) as SelectRow[];
          if (cfopRows.length > 0) setCfopOptions(cfopRows);
        } catch (err) {
          X9Logger.report(err as Error);
        }
      };

      const loadIbscbsCst = async () => {
        try {
          const { data } = await apiV2.get('/ibscbs-cst/');
          const cstRows = toSelectRowsFromList(data?.results ?? data, ['cst', 'codigo', 'codigo_cst', 'cod_cst', 'id'], [
            'descricao',
            'nome',
            'descricao_completa',
            'denominacao',
            'titulo',
          ]);
          if (cstRows.length > 0) setIbscbsCstOptions(cstRows);
        } catch (err) {
          X9Logger.report(err as Error);
        }
      };

      const loadIbscbsClasse = async () => {
        try {
          const { data } = await apiV2.get('/ibscbs-classificacao/');
          const classeRows = toSelectRowsFromList(data?.results ?? data, ['codigo', 'classe', 'classificacao', 'cod_classificacao', 'codigo_classificacao', 'id'], [
            'descricao',
            'nome',
            'descricao_completa',
            'denominacao',
            'titulo',
          ]).map((r) => ({ ...r, label: r.value }));
          if (classeRows.length > 0) setIbscbsClasseOptionsAll(classeRows);
        } catch (err) {
          X9Logger.report(err as Error);
        }
      };

      await Promise.all([loadCfop(), loadIbscbsCst(), loadIbscbsClasse()]);
    })();
  }, [open]);

  const cfopSelectValue = useMemo(() => {
    if (!form.cfop) return null;
    return cfopOptions.find((o) => o.value === form.cfop) || { label: form.cfop, value: form.cfop };
  }, [form.cfop, cfopOptions]);

  const prefixIbscbsCst = useMemo(() => prefixoDigitosIbscbsCst(form.ibscbsCst), [form.ibscbsCst]);

  const ibscbsClasseOptions = useMemo(() => {
    if (!prefixIbscbsCst) return [];
    return ibscbsClasseOptionsAll.filter((o) => String(o.value).startsWith(prefixIbscbsCst));
  }, [ibscbsClasseOptionsAll, prefixIbscbsCst]);

  const ibscbsCstSelectValue = useMemo(() => {
    if (!form.ibscbsCst) return null;
    return ibscbsCstOptions.find((o) => o.value === form.ibscbsCst) || { label: form.ibscbsCst, value: form.ibscbsCst };
  }, [form.ibscbsCst, ibscbsCstOptions]);

  const ibscbsClasseSelectValue = useMemo(() => {
    if (!form.ibscbsClasse) return null;
    return (
      ibscbsClasseOptions.find((o) => o.value === form.ibscbsClasse) ||
      ibscbsClasseOptionsAll.find((o) => o.value === form.ibscbsClasse) || {
        label: form.ibscbsClasse,
        value: form.ibscbsClasse,
      }
    );
  }, [form.ibscbsClasse, ibscbsClasseOptions, ibscbsClasseOptionsAll]);

  const unidadeSelectValue = useMemo(() => {
    const u = String(form.unidade ?? '').trim();
    if (!u) return null;
    const upper = u.toUpperCase();
    const found = UNIDADE_COMERCIAL_OPTIONS.find((o) => o.value === upper || o.value === u);
    return found || { value: upper, label: upper };
  }, [form.unidade]);

  useEffect(() => {
    const pre = prefixoDigitosIbscbsCst(form.ibscbsCst);
    if (!form.ibscbsClasse || !pre) return;
    if (String(form.ibscbsClasse).startsWith(pre)) return;
    setForm((p) => ({ ...p, ibscbsClasse: '' }));
  }, [form.ibscbsCst, form.ibscbsClasse]);

  useEffect(() => {
    if (form.quantidade > 0) {
      setForm((prev) => ({ ...prev, valorTotal: +(prev.quantidade * prev.valorUnitario).toFixed(2) }));
    } else {
      setForm((prev) => ({ ...prev, valorTotal: 0 }));
    }
  }, [form.quantidade, form.valorUnitario]);

  const submit = () => {
    if (!form.descricaoProduto.trim()) return store.notificar('Informe a descrição do produto');
    if (!form.unidade.trim()) return store.notificar('Informe a unidade');
    if (!form.quantidade || form.quantidade <= 0) return store.notificar('Informe a quantidade');
    if (form.valorUnitario < 0) return store.notificar('Valor unitário inválido');
    if (!form.codigoNcm.trim()) return store.notificar('Informe o código NCM');
    if (!form.cfop.trim()) return store.notificar('Informe o CFOP');
    if (!form.ibscbsCst.trim()) return store.notificar('Informe o IBSCBS_CST');
    if (!form.ibscbsClasse.trim()) return store.notificar('Informe o IBSCBS_CLASSE');

    onSave({ ...form });
    handleClose();
  };

  return (
    <Modal onClose={handleClose} open={open}>
      <ModalHeader>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <span>Item NFE</span>
          <IconButton onClick={handleClose} style={{ background: 'rgba(0,0,0,0.2)' }}>
            <CloseIcon />
          </IconButton>
        </div>
      </ModalHeader>
      <ModalBody style={{ width: 520, maxHeight: '85vh', overflowY: 'auto', paddingTop: 30 }}>
        <Box width="100%">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Código Produto"
                value={form.codigoProduto}
                onChange={(e) => setForm((p) => ({ ...p, codigoProduto: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descrição do Produto"
                value={form.descricaoProduto}
                onChange={(e) => setForm((p) => ({ ...p, descricaoProduto: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <NewSelect
                placeholder="Unidade"
                value={unidadeSelectValue}
                options={UNIDADE_COMERCIAL_OPTIONS}
                onChange={(e: any) => setForm((p) => ({ ...p, unidade: e?.value ? String(e.value).trim().toUpperCase() : '' }))}
                margin
              />
            </Grid>

            <Grid item xs={12}>
              <InputNumero
                label="Quantidade"
                value={form.quantidade}
                onChange={(v) => setForm((p) => ({ ...p, quantidade: v }))}
                precision="2"
              />
            </Grid>
            <Grid item xs={12}>
              <InputNumero
                label="Valor Unitário (R$)"
                value={form.valorUnitario}
                onChange={(v) => setForm((p) => ({ ...p, valorUnitario: v }))}
                precision="2"
              />
            </Grid>

            <Grid item xs={12} style={{ textAlign: 'center', paddingTop: 8, paddingBottom: 8 }}>
              <div style={{ fontSize: 22, fontWeight: 500 }}>Valor Total</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0288d1' }}>
                R$ {Formatter.formatNumber(2, form.valorTotal)}
              </div>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Código NCM"
                value={form.codigoNcm}
                onChange={(e) => setForm((p) => ({ ...p, codigoNcm: e.target.value }))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <NewSelect
                placeholder="CFOP"
                value={cfopSelectValue}
                options={cfopOptions}
                onChange={(e: any) => setForm((p) => ({ ...p, cfop: e?.value || '' }))}
                margin
              />
            </Grid>

            <Grid item xs={6}>
              <NewSelect
                placeholder="IBSCBS_CST"
                value={ibscbsCstSelectValue}
                options={ibscbsCstOptions}
                onChange={(e: any) => {
                  const cst = e?.value || '';
                  setForm((p) => {
                    const pre = prefixoDigitosIbscbsCst(cst);
                    const classeOk = Boolean(pre && p.ibscbsClasse && String(p.ibscbsClasse).startsWith(pre));
                    return { ...p, ibscbsCst: cst, ibscbsClasse: classeOk ? p.ibscbsClasse : '' };
                  });
                }}
                margin
              />
            </Grid>
            <Grid item xs={6}>
              <NewSelect
                placeholder={prefixIbscbsCst ? 'IBSCBS_CLASSE' : 'Selecione o CST antes'}
                value={ibscbsClasseSelectValue}
                options={ibscbsClasseOptions}
                onChange={(e: any) => setForm((p) => ({ ...p, ibscbsClasse: e?.value || '' }))}
                margin
                disabled={!prefixIbscbsCst}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="ICMS_CST"
                value={form.icmsCst}
                onChange={(e) => setForm((p) => ({ ...p, icmsCst: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <InputNumero
                label="ICMS %"
                value={form.icmsPercentual}
                onChange={(v) => setForm((p) => ({ ...p, icmsPercentual: v }))}
                precision="2"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="IPI_CST"
                value={form.ipiCst}
                onChange={(e) => setForm((p) => ({ ...p, ipiCst: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <InputNumero
                label="IPI %"
                value={form.ipiPercentual}
                onChange={(v) => setForm((p) => ({ ...p, ipiPercentual: v }))}
                precision="2"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="COFINS_CST"
                value={form.cofinsCst}
                onChange={(e) => setForm((p) => ({ ...p, cofinsCst: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <InputNumero
                label="COFINS %"
                value={form.cofinsPercentual}
                onChange={(v) => setForm((p) => ({ ...p, cofinsPercentual: v }))}
                precision="2"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="PIS_CST"
                value={form.pisCst}
                onChange={(e) => setForm((p) => ({ ...p, pisCst: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <InputNumero
                label="PIS %"
                value={form.pisPercentual}
                onChange={(v) => setForm((p) => ({ ...p, pisPercentual: v }))}
                precision="2"
              />
            </Grid>
          </Grid>
        </Box>
      </ModalBody>
      <Footer>
        <Botao onClick={handleClose} variant="contained" cor="#FC7467">
          Cancelar
        </Botao>
        <Botao onClick={submit} variant="contained" cor="#00C853">
          Salvar
        </Botao>
      </Footer>
    </Modal>
  );
};

export default ModalNFEAvulsaItem;
